// b24: värmen vid ankomst och avresa, och vad varje värmesystem kan.
//
// Två saker som delas av molnet, navet och appen:
//   1. Förmågorna per system (VARMEFORMAGOR). Stugkoll ska klara flera system, så varje system
//      säger vad det kan och inte kan, och appen visar bara det systemet klarar. Dokumentet
//      docs/varmesystem.md har samma uppgifter som en tabell per system, och uppdateras när ett
//      nytt system läggs till.
//   2. Platsens värmeprofil: vilket läge och vilken eko-temperatur per zon som gäller vid ankomst
//      och avresa. Förvalet är "som förut" (Komfort vid ankomst, Borta vid avresa).
//
/** Profilen som den är lagrad (null eller en trasig rad: som förut). */
export function varmeprofilFran(rad: unknown): Varmeprofil {
  if (rad === null || rad === undefined) return forvaldProfil();
  const p = tolkaVarmeprofil(rad);
  return typeof p === "string" ? forvaldProfil() : normaliseraProfil(p);
}

const medelvarde = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : null);

/**
 * Temperaturerna som värmemodellen för förvärmningen ska räkna med i en egen profil: målet är
 * medelvärdet av ankomstens eko-temperaturer (inte hubbens komfort-temperatur, som elementen kan
 * ha en annan än), och det låga läget är medelvärdet av avresans, eller frostskyddet vid Borta.
 * Null där profilen inte säger något: Komfort vid ankomst använder elementens egna inställningar,
 * och utan temperaturer gäller det som gällde förut.
 */
export function profilTemperaturer(
  p: Varmeprofil,
  bortaGrader: number,
): { komfort: number | null; lag: number | null } {
  if (p.typ !== "egen") return { komfort: null, lag: null };
  const grader = (s: Profilsida) => profilzoner(s).map((z) => z.grader);
  return {
    komfort: p.ankomst.lage === "komfort" ? null : medelvarde(grader(p.ankomst)),
    lag: p.avresa.lage === "borta" ? bortaGrader : medelvarde(grader(p.avresa)),
  };
}

// Navets kommando för en zons eko-temperatur har värdet "zon-eko:<grader>:<zon>", där zonen är
// namnet som navet skickar signalerna under (hus.zon.*@<zon>), alltså inte hubbens interna id,
// som molnet inte känner till. Graderna står först: de har aldrig kolon, så resten är zonens
// namn även om det har ett.

import { FRI_INSTANS, type NOBOLAGEN, type ZONSTYRNINGAR } from "./signaler.ts";

export const VARMESYSTEM = ["nobo", "ha"] as const;
export type Varmesystem = (typeof VARMESYSTEM)[number];

export type Nobolage = (typeof NOBOLAGEN)[number];

/** Vad hubben kan ställa i en zon (hus.zon.hubbstyrning), efter komponenternas modeller. */
export type Zonstyrning = (typeof ZONSTYRNINGAR)[number];

/** Texten i appen för en zon där hubben inte kan ställa någon temperatur. */
export const ZON_PA_ELEMENTET_TEXT = "Golvvärme eller termostat på elementet – bara läget styrs";

/**
 * De fyra fallen per zon, i en mening var. I `eko_komfort` styrs båda temperaturerna från hubben, i
 * `eko` ställs komforten på elementet men eko styrs från hubben (Nobø-appen visar "ställ in KOMFORT °C
 * på element"), i `inget` ställs båda på elementet eller golvtermostaten och bara läget styrs, och i
 * `okand` är modellen okänd (eller zonen har ingen termostat) så skrivningen tillåts och återläsningen
 * avgör.
 */
export const ZONSTYRNINGSTEXT: Record<Zonstyrning, string> = {
  eko_komfort: "Eko och komfort styrs från hubben",
  eko: "Komforten ställs på elementet, eko styrs från hubben",
  inget: ZON_PA_ELEMENTET_TEXT,
  okand: "Modellen är okänd: skrivningen tillåts och återläsningen avgör",
};

/** Zonens eko-temperatur går att styra från hubben, eller vet vi inte (då tillåts skrivningen). */
export function ekoGarAttStyra(s: Zonstyrning | null | undefined): boolean {
  return s !== "inget";
}

/** Ett globalt läge (eller en scen) som systemet har. */
export interface Varmelage {
  id: string;
  namn: string;
  /** Vad läget betyder, i en mening. */
  text: string;
}

export interface Varmeformaga {
  system: Varmesystem;
  namn: string;
  /** De globala lägena (Nobø) eller scenerna (Home Assistant) som systemet har. */
  lagen: Varmelage[];
  /** Kan en temperatur skrivas per zon, och i så fall för vilket läge. */
  zontemperatur: { eko: boolean; komfort: boolean };
  /** Gränserna för en temperatur per zon, i hela grader, eller null om den inte kan skrivas. */
  zongrans: { min: number; max: number } | null;
  /** Vad Borta betyder i grader, eller null om systemet inte har ett sådant läge. */
  borta: { text: string; min: number; max: number } | null;
  veckoprogram: { finns: boolean; kan_skrivas: boolean; text: string };
  /** Lägena som en egen profil kan välja vid ankomst och avresa, eller null: ingen egen profil. */
  profil: { ankomst: string[]; avresa: string[] } | null;
  /** Kända begränsningar, som appen visar under profilen. */
  begransningar: string[];
}

/** Gränserna för eko-temperaturen per zon i Nobø: heltal 7–30 (specifikationen, bilaga A, Zone). */
export const ZONGRANS = { min: 7, max: 30 } as const;

export const VARMEFORMAGOR: Record<Varmesystem, Varmeformaga> = {
  nobo: {
    system: "nobo",
    namn: "Nobø",
    lagen: [
      { id: "normal", namn: "Normal", text: "Zonerna följer sitt veckoprogram" },
      {
        id: "komfort",
        namn: "Komfort",
        text: "Komfort-temperaturen gäller i alla zoner (eller elementets egen, om den är inställd på elementet)",
      },
      { id: "eko", namn: "Eko", text: "Eko-temperaturen gäller i alla zoner" },
      { id: "borta", namn: "Borta", text: "Frostskydd i alla zoner" },
    ],
    zontemperatur: { eko: true, komfort: false },
    zongrans: { ...ZONGRANS },
    borta: { text: "Frostskydd", min: 7, max: 8 },
    veckoprogram: {
      finns: true,
      kan_skrivas: false,
      text: "Ett veckoprogram per zon. Stugkoll läser det men ändrar det inte.",
    },
    profil: { ankomst: ["normal", "eko", "komfort"], avresa: ["eko", "borta", "normal"] },
    begransningar: [
      'Komfort kan vara inställd på elementet (Nobø-appen visar då "ställ in KOMFORT °C på element"). Hubbens komfort-värde används då inte, och Stugkoll skriver aldrig komfort-temperaturen.',
      "Hubben vet inte vad elementets termostat står på, eller om elementet är avstängt. Stugkoll styr därför med eko-temperaturen och läget Normal.",
      "I rum med golvvärme (golvtermostat), och i zoner där både komfort och eko ställs på elementet, går det inte att ändra temperaturen från hubben, bara läget. Hubben rapporterar ändå värden för eko och komfort, men de används inte.",
      "Vad hubben kan ställa i en zon avgörs av komponenternas modeller (modellkoden är de tre första siffrorna i serienumret). Är en modell okänd tillåter Stugkoll skrivningen och litar på återläsningen.",
      "Borta är frostskydd, cirka 7–8 °C, och kan inte ställas in.",
      "Normal följer zonens veckoprogram, som Stugkoll inte ändrar.",
    ],
  },
  ha: {
    system: "ha",
    namn: "Home Assistant",
    lagen: [
      { id: "forvarm", namn: "Förvärmning", text: "Scenen för förvärmning körs" },
      { id: "hemma", namn: "Hemma", text: "Scenen för hemma körs" },
      { id: "borta", namn: "Borta", text: "Scenen för borta körs" },
    ],
    zontemperatur: { eko: false, komfort: false },
    zongrans: null,
    borta: null,
    veckoprogram: { finns: false, kan_skrivas: false, text: "Stugkoll känner inget veckoprogram." },
    profil: null,
    begransningar: [
      "Stugkoll kör scenerna som navet har valt. Temperaturen per zon ställs in i Home Assistant.",
    ],
  },
};

/** Lägena som en egen profil kan välja, för Nobø. */
export const PROFILLAGEN_ANKOMST = VARMEFORMAGOR.nobo.profil?.ankomst ?? [];
export const PROFILLAGEN_AVRESA = VARMEFORMAGOR.nobo.profil?.avresa ?? [];

export const PROFILTYPER = ["som_forut", "egen"] as const;
export type Profiltyp = (typeof PROFILTYPER)[number];

/** Det som gäller vid ankomsten eller avresan i en egen profil. */
export interface Profilsida {
  lage: Nobolage;
  /** Eko-temperaturen per zon, eller null för "rör inte". En zon som saknas skrivs inte heller. */
  zoner: Record<string, number | null>;
}

export interface Varmeprofil {
  typ: Profiltyp;
  ankomst: Profilsida;
  avresa: Profilsida;
}

/** Förvalet för alla platser: som förut. Sidorna har ingen betydelse förrän profilen är egen. */
export function forvaldProfil(): Varmeprofil {
  return {
    typ: "som_forut",
    ankomst: { lage: "komfort", zoner: {} },
    avresa: { lage: "borta", zoner: {} },
  };
}

/** Den egna profilen är på: bara då skiljer sig planerna från förut. */
export function arEgenProfil(p: Varmeprofil | null | undefined): p is Varmeprofil {
  return p?.typ === "egen";
}

/** Zonerna i en profilsida som får en temperatur, i ordning. */
export function profilzoner(s: Profilsida): { zon: string; grader: number }[] {
  return Object.entries(s.zoner)
    .filter((e): e is [string, number] => typeof e[1] === "number")
    .map(([zon, grader]) => ({ zon, grader }));
}

const MAX_ZONER = 40;

/** Kontrollerar en temperatur: ett heltal 7–30. Returnerar ett fel eller null. */
export function kontrolleraZontemp(v: unknown): string | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return "Temperaturen ska vara ett tal";
  if (!Number.isInteger(v)) return "Temperaturen ska vara hela grader";
  if (v < ZONGRANS.min || v > ZONGRANS.max) {
    return `Temperaturen ska vara ${ZONGRANS.min}–${ZONGRANS.max} °C`;
  }
  return null;
}

function tolkaSida(
  x: unknown,
  namn: string,
  tillatna: string[],
): { sida: Profilsida } | { fel: string } {
  if (typeof x !== "object" || x === null || Array.isArray(x)) return { fel: `${namn} saknas` };
  const o = x as Record<string, unknown>;
  if (typeof o.lage !== "string" || !tillatna.includes(o.lage)) {
    return { fel: `Läget vid ${namn} ska vara något av ${tillatna.join(", ")}` };
  }
  const z = o.zoner ?? {};
  if (typeof z !== "object" || z === null || Array.isArray(z)) {
    return { fel: `Zonerna vid ${namn} ska vara en lista` };
  }
  const poster = Object.entries(z as Record<string, unknown>);
  if (poster.length > MAX_ZONER) return { fel: `Högst ${MAX_ZONER} zoner` };
  const zoner: Record<string, number | null> = {};
  const sett = new Set<string>();
  for (const [rawNamn, v] of poster) {
    const zon = rawNamn.trim();
    if (!FRI_INSTANS.test(zon)) return { fel: "Zonens namn ska vara 1–40 tecken, utan @" };
    if (sett.has(zon.toLowerCase())) return { fel: `Zonen ${zon} finns två gånger` };
    sett.add(zon.toLowerCase());
    if (v === null) {
      zoner[zon] = null;
      continue;
    }
    const fel = kontrolleraZontemp(v);
    if (fel) return { fel: `${zon}: ${fel}` };
    zoner[zon] = v as number;
  }
  return { sida: { lage: o.lage as Nobolage, zoner } };
}

/**
 * Kontrollerar en profil från appen. Returnerar profilen, rensad, eller ett felmeddelande. Är
 * typen "som_forut" ignoreras sidorna (förvalet gäller).
 */
export function tolkaVarmeprofil(x: unknown): Varmeprofil | string {
  if (typeof x !== "object" || x === null || Array.isArray(x)) return "Profilen saknas";
  const o = x as Record<string, unknown>;
  if (!(PROFILTYPER as readonly string[]).includes(o.typ as string)) {
    return "Välj som förut eller egen profil";
  }
  if (o.typ === "som_forut") return forvaldProfil();
  const ankomst = tolkaSida(o.ankomst, "ankomsten", PROFILLAGEN_ANKOMST);
  if ("fel" in ankomst) return ankomst.fel;
  const avresa = tolkaSida(o.avresa, "avresan", PROFILLAGEN_AVRESA);
  if ("fel" in avresa) return avresa.fel;
  return { typ: "egen", ankomst: ankomst.sida, avresa: avresa.sida };
}

/** Samma profil, med samma innehåll i samma ordning, så att två lika profiler jämförs lika. */
export function normaliseraProfil(p: Varmeprofil): Varmeprofil {
  const sida = (s: Profilsida): Profilsida => ({
    lage: s.lage,
    zoner: Object.fromEntries(Object.entries(s.zoner).sort(([a], [b]) => a.localeCompare(b, "sv"))),
  });
  return { typ: p.typ, ankomst: sida(p.ankomst), avresa: sida(p.avresa) };
}

// Navets kommando för en zons eko-temperatur

export const ZON_EKO = "zon-eko";
const ZON_EKO_RAD = /^zon-eko:(-?\d+(?:\.\d+)?):(.+)$/s;

export function zonEkoVarde(zon: string, grader: number): string {
  return `${ZON_EKO}:${grader}:${zon}`;
}

export function arZonEko(varde: string): boolean {
  return varde.startsWith(`${ZON_EKO}:`);
}

/** Zonen och graderna i ett kommando, eller null om värdet inte har formen. */
export function tolkaZonEko(varde: string): { zon: string; grader: number } | null {
  const m = ZON_EKO_RAD.exec(varde);
  if (!m) return null;
  return { grader: Number(m[1]), zon: m[2] as string };
}

/** Kommandot som en mening, till exempel "Eko-temperaturen i sovrum sätts till 18 °C". */
export function zonEkoText(varde: string): string {
  const z = tolkaZonEko(varde);
  return z ? `Eko-temperaturen i ${z.zon} sätts till ${z.grader} °C` : `Zon-eko ${varde}`;
}

/** En zon som navet har sagt (hus.zon.*@<zon>), med värdena i hubben nu. */
export interface Profilzon {
  namn: string;
  komfort: number | null;
  eko: number | null;
  /** Vad hubben kan ställa i zonen (hus.zon.hubbstyrning), eller null när navet inte har sagt det. */
  styrning: Zonstyrning | null;
}

/** Det appen visar under Värmen vid ankomst och avresa. */
export interface Varmeprofilvisning {
  profil: Varmeprofil;
  /** Förmågorna för värmesystemen som platsen har. Tom: platsen har inget som kan styras. */
  formagor: Varmeformaga[];
  /** Nobø-zonerna, med de namn som Nobø har. */
  zoner: Profilzon[];
  /** Platsens system klarar en egen profil (temperatur per zon). */
  egenProfil: boolean;
}
